import React, { useEffect, useState } from "react";
import Template from "../utils/Template";
import Card from "../utils/Card";
import TableScroll from "../utils/TableScroll";
import { Table, TableBody, TableHeader } from "../utils/Table";
import { getMethod, postMethod, postMethodUpload } from "../service";
import { decodeData, errorToaster, successToaster } from "../utils/Helper";
import { useNavigate } from "react-router-dom";
import ActionModal from "../utils/modals/ActionModal";
import {
  IMAGE_PROXY_API,
  PAYMENT_PROVIDERS_API,
  PAYMENT_PROVIDERS_CUD_API,
} from "../service/constant";
import Switch from "../utils/Switch";

const PaymentProviders = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [formData, setFormData] = useState({
    bank_type: "",
    bank_acc_name: "",
    bank_acc_number: "",
    logo: "",
    state: "enable",
  });
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();

  const fetchPaymentProviders = async () => {
    setIsLoading(true);
    const token = decodeData(localStorage.getItem("r_c_a"));
    if (token) {
      const res = await getMethod(`${PAYMENT_PROVIDERS_API}`, token.token);
      if (res?.isSuccess) {
        setPaymentProviders(res.data);
      } else {
        errorToaster("Failed to fetch payment providers");
      }
      setIsLoading(false);
    } else {
      setIsLoading(false);
      errorToaster("Unauthenticated!");
      setTimeout(() => {
        navigate("/login");
      }, 100);
    }
  };

  useEffect(() => {
    fetchPaymentProviders();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSwitchChange = (id, currentState) => {
    const newState = currentState === "enable" ? "disable" : "enable";
    setPaymentProviders(
      paymentProviders.map((provider) =>
        provider._id === id ? { ...provider, state: newState } : provider
      )
    );

    updateProviderState(id, newState);
  };

  const updateProviderState = async (id, newState) => {
    setIsLoading(true);
    const token = decodeData(localStorage.getItem("r_c_a"));
    if (token) {
      try {
        const res = await postMethod(
          `${PAYMENT_PROVIDERS_CUD_API}/${id}`,
          { state: newState },
          token.token
        );
        if (res?.isSuccess) {
          successToaster(res?.message);
        } else {
          errorToaster(res?.message || "Something went wrong!");
        }
      } catch (error) {
        errorToaster("An error occurred");
      } finally {
        setIsLoading(false);
      }
    } else {
      errorToaster("Unauthenticated!");
    }
  };

  const handleEdit = (provider) => {
    setFormData({
      bank_type: provider.bank_type,
      bank_acc_name: provider.bank_acc_name,
      bank_acc_number: provider.bank_acc_number,
      logo: provider.logo,
    });
    setEditId(provider._id);
    setShowEditModal(true);
  };

  const handleSubmit = async () => {
    if (!editId && !file) {
      errorToaster("Payment provider image is required.");
      return;
    }
    setIsLoading(true);
    const token = decodeData(localStorage.getItem("r_c_a"));

    const form = new FormData();
    form.append("bank_type", formData.bank_type);
    form.append("bank_acc_name", formData.bank_acc_name);
    form.append("bank_acc_number", formData.bank_acc_number);

    if (file) {
      form.append("logo", file);
    }

    if (token) {
      try {
        const edit_api = editId ? `/${editId}` : "";
        const res = await postMethodUpload(
          `${PAYMENT_PROVIDERS_CUD_API}${edit_api}`,
          form,
          token.token
        );
        if (res?.isSuccess) {
          successToaster(res?.message);
          setEditId(null);
          setShowEditModal(false);
          fetchPaymentProviders();
        } else {
          errorToaster(res?.message);
        }
      } catch (error) {
        errorToaster(error?.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      errorToaster("Unauthenticated!");
    }
  };

  const handleCreate = () => {
    setFormData({
      bank_type: "",
      bank_acc_name: "",
      bank_acc_number: "",
      logo: "",
    });
    setFile(null);
    setEditId(null);
    setShowEditModal(true);
  };

  return (
    <Template title={"Payment Providers"} isLoading={isLoading}>
      <Card>
        <button className="btn btn-primary mb-3" onClick={handleCreate}>
          Add Payment Provider
        </button>
        <TableScroll>
          <Table>
            <TableHeader>
              <th>Bank Type</th>
              <th>Account Name</th>
              <th>Account Number</th>
              <th>Logo</th>
              <th>State</th>
              <th>Actions</th>
            </TableHeader>
            <TableBody>
              {paymentProviders.length > 0 &&
                paymentProviders.map((provider) => (
                  <tr key={provider._id} style={{ verticalAlign: "middle" }}>
                    <td>{provider.bank_type}</td>
                    <td>{provider.bank_acc_name}</td>
                    <td>{provider.bank_acc_number}</td>
                    <td>
                      <img
                        src={IMAGE_PROXY_API(provider.logo)}
                        alt="Logo"
                        width="50"
                      />
                    </td>
                    <td>
                      <Switch
                        status={provider.state === "enable"}
                        event={() =>
                          handleSwitchChange(provider._id, provider.state)
                        }
                        bg_color={
                          provider.state === "enable" ? "#4CAF50" : "#f44336"
                        }
                        id={`switch-${provider._id}`}
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-warning btn-sm py-1"
                        onClick={() => handleEdit(provider)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
            </TableBody>
          </Table>
        </TableScroll>
      </Card>

      {showEditModal && (
        <ActionModal
          isLoading={isLoading}
          onSubmit={handleSubmit}
          onClose={() => setShowEditModal(false)}
          name={editId ? "Edit Payment Provider" : "Add Payment Provider"}
        >
          <form onSubmit={(e) => e.preventDefault()}>
            <input
              name="bank_type"
              value={formData.bank_type}
              onChange={handleInputChange}
              placeholder="Bank Type"
              className="form-control mb-2"
              required
            />
            <input
              name="bank_acc_name"
              value={formData.bank_acc_name}
              onChange={handleInputChange}
              placeholder="Bank Account Name"
              className="form-control mb-2"
              required
            />
            <input
              name="bank_acc_number"
              type="number"
              value={formData.bank_acc_number}
              onChange={handleInputChange}
              placeholder="Bank Account Number"
              className="form-control mb-2"
              required
            />
            <input
              type="file"
              name="logo"
              onChange={handleFileChange}
              className="form-control mb-2"
              required
            />
          </form>
        </ActionModal>
      )}
    </Template>
  );
};

export default PaymentProviders;
